/**
 * Input validation utilities for API parameters
 */

/**
 * Validates and sanitizes URL parameter values
 * @param key - Parameter key
 * @param value - Parameter value
 * @returns Sanitized value or null if invalid
 */
export function validateUrlParam(key: string, value: any): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  // Convert to string
  const stringValue = String(value);

  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/i, // Script tags
    /javascript:/i, // JavaScript protocol
    /on\w+\s*=/i, // Event handlers (onclick, onerror, etc.)
    /data:text\/html/i, // Data URLs with HTML
    /vbscript:/i, // VBScript
    /expression\(/i, // CSS expressions
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(stringValue)) {
      return null; // Reject dangerous values
    }
  }

  // Limit length to prevent abuse
  if (stringValue.length > 1000) {
    return stringValue.substring(0, 1000);
  }

  return stringValue;
}

/**
 * Validates parameter key name
 * @param key - Parameter key
 * @returns true if key is valid
 */
export function isValidParamKey(key: string): boolean {
  // Only allow alphanumeric, underscore, and hyphen
  return /^[a-zA-Z0-9_-]+$/.test(key) && key.length <= 50;
}

/**
 * Sanitizes all URL parameters
 * @param params - Parameters object
 * @returns Sanitized parameters object
 */
export function sanitizeUrlParams(params: Record<string, any>): Record<string, string> {
  const sanitized: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    // Validate key
    if (!isValidParamKey(key)) {
      return; // Skip invalid keys
    }

    // Validate and sanitize value
    const sanitizedValue = validateUrlParam(key, value);
    if (sanitizedValue !== null) {
      sanitized[key] = sanitizedValue;
    }
  });

  return sanitized;
}

/**
 * Debounce time in milliseconds
 */
export const DEBOUNCE_TIME = 1000;